import { useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  myDefaultOrganizationIdQuery,
  setDefaultOrganizationMutation,
} from '@/graph/defaultOrganization';

const DEFAULT_ORG_STORAGE_KEY = 'defaultOrganizationId';

export function getCachedDefaultOrganizationId(): number | null {
  const cached = localStorage.getItem(DEFAULT_ORG_STORAGE_KEY);
  return cached ? parseInt(cached, 10) : null;
}

interface Membership {
  id: number | string;
  organization: {
    id: number | string;
    name: string;
  };
  metadata?: string[];
}

interface UseDefaultOrganizationResult {
  defaultOrganizationId: number | null;
  loading: boolean;
  setDefaultOrganization: (organizationId: number | null) => Promise<void>;
  clearDefaultOrganization: () => Promise<void>;
  isDefaultOrganization: (organizationId: number | string) => boolean;
  sortMembershipsByDefault: <T extends Membership>(memberships: T[]) => T[];
  getDefaultMembership: <T extends Membership>(memberships: T[]) => T | undefined;
}

export function useDefaultOrganization(): UseDefaultOrganizationResult {
  const { data, loading, refetch } = useQuery(myDefaultOrganizationIdQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const [setDefaultMutation] = useMutation(setDefaultOrganizationMutation);

  const defaultOrganizationId: number | null = data?.myDefaultOrganizationId ?? null;

  useEffect(() => {
    if (defaultOrganizationId !== null) {
      localStorage.setItem(DEFAULT_ORG_STORAGE_KEY, String(defaultOrganizationId));
    } else if (data?.myDefaultOrganizationId === null) {
      localStorage.removeItem(DEFAULT_ORG_STORAGE_KEY);
    }
  }, [defaultOrganizationId, data]);

  const setDefaultOrganization = useCallback(
    async (organizationId: number | null) => {
      await setDefaultMutation({
        variables: { organizationId },
        update: (cache) => {
          cache.writeQuery({
            query: myDefaultOrganizationIdQuery,
            data: { myDefaultOrganizationId: organizationId },
          });
        },
      });
      if (organizationId !== null) {
        localStorage.setItem(DEFAULT_ORG_STORAGE_KEY, String(organizationId));
      } else {
        localStorage.removeItem(DEFAULT_ORG_STORAGE_KEY);
      }
    },
    [setDefaultMutation]
  );

  const clearDefaultOrganization = useCallback(async () => {
    await setDefaultOrganization(null);
  }, [setDefaultOrganization]);

  const isDefaultOrganization = useCallback(
    (organizationId: number | string): boolean => {
      if (defaultOrganizationId === null) return false;
      return String(organizationId) === String(defaultOrganizationId);
    },
    [defaultOrganizationId]
  );

  const sortMembershipsByDefault = useCallback(
    <T extends Membership>(memberships: T[]): T[] => {
      if (!memberships || memberships.length === 0) return memberships;
      if (defaultOrganizationId === null) return memberships;

      return [...memberships].sort((a, b) => {
        const aIsDefault = String(a.organization.id) === String(defaultOrganizationId);
        const bIsDefault = String(b.organization.id) === String(defaultOrganizationId);

        if (aIsDefault && !bIsDefault) return -1;
        if (!aIsDefault && bIsDefault) return 1;
        return 0;
      });
    },
    [defaultOrganizationId]
  );

  const getDefaultMembership = useCallback(
    <T extends Membership>(memberships: T[]): T | undefined => {
      if (!memberships || defaultOrganizationId === null) return undefined;
      return memberships.find(
        m => String(m.organization.id) === String(defaultOrganizationId)
      );
    },
    [defaultOrganizationId]
  );

  return {
    defaultOrganizationId,
    loading,
    setDefaultOrganization,
    clearDefaultOrganization,
    isDefaultOrganization,
    sortMembershipsByDefault,
    getDefaultMembership,
  };
}
